import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDrawerFromCatalogRequest,
  CreateDrawerRequest,
  UpdateDrawerRequest,
} from '@kreart/shared';
import { invalidateProjectBom, projectsQueryKeys } from '@/features/projects/hooks';
import { cabinetsQueryKeys } from '@/features/project-items/cabinets/hooks';
import { drawersApi } from './api';

const ROOT = ['project-drawers'] as const;
export const drawersQueryKeys = {
  all: ROOT,
  list: (projectId: string, cabinetId: string) => [...ROOT, 'list', projectId, cabinetId] as const,
};

export function useDrawers(
  projectId: string | undefined,
  cabinetId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: drawersQueryKeys.list(projectId ?? 'nil', cabinetId ?? 'nil'),
    queryFn: () => drawersApi.list(projectId!, cabinetId!),
    enabled: enabled && Boolean(projectId && cabinetId),
    meta: { silent: true },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, projectId: string, cabinetId: string) {
  void qc.invalidateQueries({ queryKey: drawersQueryKeys.list(projectId, cabinetId) });
  void qc.invalidateQueries({ queryKey: [...cabinetsQueryKeys.all, 'list', projectId] });
  void qc.invalidateQueries({ queryKey: cabinetsQueryKeys.detail(projectId, cabinetId) });
  void qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) });
  invalidateProjectBom(qc, projectId);
}

export function useCreateDrawer(projectId: string, cabinetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDrawerRequest) => drawersApi.create(projectId, cabinetId, body),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId, cabinetId),
  });
}

export function useCreateDrawerFromCatalog(projectId: string, cabinetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDrawerFromCatalogRequest) =>
      drawersApi.createFromCatalog(projectId, cabinetId, body),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId, cabinetId),
  });
}

export function useUpdateDrawer(projectId: string, cabinetId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDrawerRequest) => drawersApi.update(projectId, cabinetId, id, body),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId, cabinetId),
  });
}

export function useDeleteDrawer(projectId: string, cabinetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => drawersApi.remove(projectId, cabinetId, id),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId, cabinetId),
  });
}
